const SAFE_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:"]);
const SAFE_IMAGE_PROTOCOLS = new Set(["https:"]);
const RELATIVE_URL_BASE = "https://ai-content.invalid";

function escapeHtml(value) {
  return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function safeUrl(value, allowedProtocols) {
  if (typeof value !== "string" || value.trim() === "") return null;

  try {
    const url = new URL(value, RELATIVE_URL_BASE);
    return allowedProtocols.has(url.protocol) ? value : null;
  } catch {
    return null;
  }
}

// Marked calls renderer methods with its parser as `this.parser`, so link text
// keeps normal inline markdown while destinations are restricted to navigation
// schemes. Unsafe links become plain text instead of clickable code/data URLs.
//
// Images create a no-click network request, so they are opt-in: model-authored
// URLs must never become browser requests (retrieved business data can contain
// prompt injection that asks the model to place sensitive values in that URL),
// while author-controlled content like academy articles legitimately needs
// https images.
export function createSafeMarkdownRenderer({ allowImages = false } = {}) {
  return {
    html({ text }) {
      return escapeHtml(text);
    },

    link({ href, title, tokens }) {
      const text = this.parser.parseInline(tokens);
      const destination = safeUrl(href, SAFE_LINK_PROTOCOLS);
      if (destination === null) return text;

      const titleAttribute = title ? ` title="${escapeHtml(title)}"` : "";
      return `<a href="${escapeHtml(destination)}"${titleAttribute} rel="noopener noreferrer">${text}</a>`;
    },

    image({ href, title, text }) {
      if (!allowImages) return escapeHtml(text);

      const source = safeUrl(href, SAFE_IMAGE_PROTOCOLS);
      if (source === null) return escapeHtml(text);

      const titleAttribute = title ? ` title="${escapeHtml(title)}"` : "";
      return `<img src="${escapeHtml(source)}" alt="${escapeHtml(text)}"${titleAttribute} loading="lazy" />`;
    },
  };
}

export const aiMarkdownRenderer = createSafeMarkdownRenderer();

export function neutralizeSpreadsheetFormula(value) {
  if (typeof value !== "string") return value;

  // Spreadsheet applications may ignore leading whitespace before deciding a
  // cell is a formula. An apostrophe forces text and is hidden by common apps.
  let formulaStart = 0;
  while (formulaStart < value.length && value.charCodeAt(formulaStart) <= 0x20) formulaStart += 1;

  return "=+-@".includes(value[formulaStart]) ? `'${value}` : value;
}

function encodeCsvCell(value) {
  let normalized = value ?? "";
  if (typeof normalized === "object") normalized = JSON.stringify(normalized);

  normalized = String(neutralizeSpreadsheetFormula(normalized));

  return `"${normalized.replace(/"/g, '""')}"`;
}

export function serializeCsv(rows) {
  if (!rows.length) return "";

  const columns = Object.keys(rows[0]);
  return [columns.map(encodeCsvCell).join(","), ...rows.map((row) => columns.map((column) => encodeCsvCell(row[column])).join(","))].join("\n");
}
